// Поле выбора из справочника. Обёртка над примитивом дизайн-системы: подпись, состояние
// «справочник ещё едет», состояние «справочник пуст» и необязательный пункт «не выбрано».
//
// Пустой справочник — это НЕ пустой список вариантов, а причина, по которой дальше не
// пройти: «юрлиц нет, заведите юрлицо в настройках». Молчаливый пустой раскрывающийся
// список выглядит как поломка (правило 7).

import { useEffect, type ReactNode } from 'react';

import { RiErrorWarningLine } from '@remixicon/react';

import * as Hint from '@/shared/ui/hint';
import * as Label from '@/shared/ui/label';
import * as Select from '@/shared/ui/select';

/** Значение пункта «не выбрано». Пустая строка примитиву выбора запрещена. */
export const NO_VALUE = 'none';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectFieldProps {
  id: string;
  label: string;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  options: readonly SelectOption[];
  /** Пункт «не выбрано» с этой подписью. Не задан — выбор обязателен. */
  noneLabel?: string;
  placeholder: string;
  /** Справочник ещё едет: список пуст не потому, что вариантов нет. */
  pending?: boolean;
  /**
   * Справочник НЕ ПРИЕХАЛ: запрос упал.
   *
   * Третий факт, а не второй. Без него пустой список после отказа сервера показывал
   * `emptyNote` — «юридических лиц в справочнике нет, их заводит администратор», — то есть
   * отправлял человека настраивать то, что настроено. Поймано снимком состояния ошибки
   * 26.08.2026 на пульте подотчёта.
   */
  failed?: boolean;
  /**
   * Сама ошибка, если она есть. По ней различается ПОЧЕМУ справочник не приехал.
   *
   * До 02.09.2026 сообщение было одно на все случаи: «связь с сервером прервалась, обновите
   * экран». На прогоне девяти ролей это оказалось прямой неправдой. Охранник открывает свой
   * реестр, отбор по объекту падает с отказом прав — справочник объектов ему не положен, —
   * и экран советует ему обновиться. Он обновляет, и так до бесконечности: связь исправна,
   * а отбор ему не откроется никогда.
   */
  failedError?: { status: number | null } | null;
  /** Что написать, если справочник приехал пустым: причина и следующий шаг. */
  emptyNote?: string;
  error?: string;
  hint?: ReactNode;
}

export function SelectField({
  id,
  label,
  required,
  value,
  onChange,
  options,
  noneLabel,
  placeholder,
  pending = false,
  failedError = null,
  failed = false,
  emptyNote,
  error,
  hint,
}: SelectFieldProps) {
  const isEmpty = !pending && !failed && options.length === 0;

  // Справочник с ЕДИНСТВЕННЫМ вариантом — это не вопрос, а формальность: ответ у него один.
  // Замер 26.08.2026 по мастеру постановки на объект: юрлицо в системе одно (ООО ЧОП «Барк»),
  // и человек всё равно платил за него два нажатия — раскрыть список и ткнуть в единственную
  // строку. То же в каждом мастере, где справочник короткий. Подставляем сами, и выбранное
  // при этом ВИДНО в поле: человек читает, что подставилось, а не догадывается.
  //
  // Не трогаем два случая. Когда у поля есть пункт «не выбрано» (`noneLabel`), пустой выбор —
  // законный ответ, и подставлять за человека нельзя. Когда поле необязательное — тем более.
  useEffect(() => {
    if (required !== true || noneLabel !== undefined || pending) return;
    const единственный = options.length === 1 ? options[0] : undefined;
    if (value !== NO_VALUE || единственный === undefined) return;
    onChange(единственный.value);
  }, [required, noneLabel, pending, value, options, onChange]);

  return (
    <div className='flex flex-col gap-1'>
      <Label.Root htmlFor={id}>
        {label}
        {required === true && <Label.Asterisk />}
      </Label.Root>
      {/* Значение «не выбрано» уходит вниз как ОТСУТСТВИЕ значения, если пункта «не выбрано»
          в списке нет. Так бывает у обязательных полей: `noneLabel` им не задают, а начальным
          состоянием ставят `NO_VALUE`. Примитив выбора при этом получал значение, которому не
          соответствует ни один пункт, и рисовал пустоту — ни выбранного, ни подсказки. На
          живом экране обязательное поле «Юридическое лицо» в мастере выдачи выглядело просто
          сломанным: пустая рамка со звёздочкой. */}
      <Select.Root
        size='small'
        hasError={error !== undefined}
        value={value === NO_VALUE && noneLabel === undefined ? undefined : value}
        onValueChange={onChange}
        disabled={pending || isEmpty || failed}
      >
        <Select.Trigger id={id}>
          <Select.Value
            placeholder={
              pending ? 'Загружаем справочник…' : failed ? 'Справочник не приехал' : placeholder
            }
          />
        </Select.Trigger>
        <Select.Content>
          {noneLabel !== undefined && <Select.Item value={NO_VALUE}>{noneLabel}</Select.Item>}
          {options.map((option) => (
            <Select.Item key={option.value} value={option.value}>
              {option.label}
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Root>
      {failed && (
        // «Не приехал» вместо «пуст»: иначе человек идёт заводить справочник, который заведён.
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {failedError?.status === 403
            ? 'Этот отбор вашей роли не открыт: справочник ведут те, кто отвечает за него. Обновление экрана здесь не поможет.'
            : 'Справочник не приехал: связь с сервером прервалась. Обновите экран.'}
        </Hint.Root>
      )}
      {isEmpty && emptyNote !== undefined && (
        <Hint.Root>
          <Hint.Icon as={RiErrorWarningLine} />
          {emptyNote}
        </Hint.Root>
      )}
      {error !== undefined && (
        <Hint.Root hasError>
          <Hint.Icon as={RiErrorWarningLine} />
          {error}
        </Hint.Root>
      )}
      {hint !== undefined && error === undefined && <Hint.Root>{hint}</Hint.Root>}
    </div>
  );
}
